import {
    Add,
    Download,
    EditOffOutlined,
    EditOutlined,
} from "@mui/icons-material";

import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
    FormEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { AppContext } from "../../../Utilities/AppContext";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import { S3Service } from "../../../Utilities/S3Service";
import { getSelectedFilters } from "../../Customer/Property/Property";
import { getReportTemplates } from "../../Payment/Payment";
import BaseTable from "../../Table/BaseTable";
import {
    currencyFormatter,
    numberFormatter,
} from "../Bill/Cells/Last12Summary";
import { dateFormatter, monthFormatter } from "../Bill/SingleBill";
import {
    initialClearAll,
    initialIntermediateFilter,
    ReadingFilterForm,
} from "./ReadingFilterForm";
import { ShowReadingImage } from "./ShowReadingImage";

export const downloadReadingImageFile = async (reading: any) => {
    const readingKey = `readings/${reading.id}`;
    const [data, err] = await S3Service.getS3ByKey(readingKey);

    if (!data || err || !data.url) {
        alert("No image uploaded or failed to get image link.");
        return;
    }

    const response = await fetch(data.url);
    const blob = await response.blob();

    const urlPath = new URL(data.url).pathname;
    const extension =
        urlPath.slice(((urlPath.lastIndexOf(".") - 1) >>> 0) + 2) || "jpg";

    const meterNo = reading.meter?.meterNo || "unknown_meter";

    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const billingMonth = monthNames[reading.month - 1] || "unknown_month";
    const year = reading.year || "unknown_year";

    const filename = `${meterNo}_${billingMonth}_${year}.${extension}`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const DownloadReadingImage = ({ reading }: any) => {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadReadingImageFile(reading);
        } catch (error) {
            console.error("Error downloading image:", error);
            alert("Error downloading image.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <IconButton
            onClick={handleDownload}
            disabled={downloading}
            size="small"
        >
            {downloading ? (
                <CircularProgress size="1.2rem" />
            ) : (
                <Download fontSize="small" />
            )}
        </IconButton>
    );
};

export const readings = ({ pagination, filter }: any) => {
    const { wrongReadingFlag, ...cleanFilter } = filter;
    return GET("/reading", {
        page: pagination.page + 1,
        limit: pagination.limit,
        ...Object.fromEntries(
            Object.entries(cleanFilter).filter(
                (entry) => entry[1] && entry[1] !== "all"
            )
        ),
        connectionType: filter["connection type"],
        "connection type": undefined,
        wrongReadingFlag: wrongReadingFlag == true ? 1 : undefined,
    });
};

type ReportTemplate = {
    id: number;
    key: string;
    value: string[];
};

const initialDefaultRegions = {
    district: "",
    village: "",
    hamlet: "",
};

export function formatDateTime(isoString: any) {
    const date = new Date(isoString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${year}-${month}-${day} ${String(hours).padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
}

const getUnitPriceAdaptive = (row: any) => {
    let unitsAmount = 0;
    let unitsRemaining = row.consumedUnits;
    let unitPrice = [];

    const componentsByConsumption: {
        [consumption: string]: any[];
    } = {};

    for (const comp of row.meter.priceType.unitChargeRule.components) {
        if (
            Object.keys(componentsByConsumption).includes(
                comp.consumption.toString()
            )
        ) {
            componentsByConsumption[comp.consumption].push(comp);
        } else {
            componentsByConsumption[comp.consumption] = [comp];
        }
    }

    const maximumRange = Object.keys(componentsByConsumption)
        .map((c) => parseInt(c))
        .sort((a, b) => b - a)[0];
    const targetSubCategory =
        row.currentUnits > maximumRange ? "-1" : maximumRange.toString();

    const targetComps = componentsByConsumption[targetSubCategory].sort(
        (a, b) => a.start - b.start
    );

    for (const comp of targetComps) {
        if (unitsRemaining === 0) break;

        const targetCost = comp.cost;

        let unitsInRange = 0;

        if (comp.end == -1) {
            unitsInRange = unitsRemaining;
            unitsRemaining = 0;
        } else {
            const compRange = comp.end - Math.max(0, comp.start - 1);

            if (unitsRemaining - compRange < 0) {
                unitsInRange = unitsRemaining;
                unitsRemaining = 0;
            } else {
                unitsInRange = compRange;
                unitsRemaining -= compRange;
            }
        }

        unitsAmount += targetCost * unitsInRange;

        unitPrice.push([unitsInRange, targetCost]);
    }

    return [unitsAmount, unitPrice];
};

// TODO Add adaptive surcharge calculation
const getUnitPrice = (row: any) => {
    const intUnits = row.consumedUnits;
    const components = row.meter.priceType.unitChargeRule.components;
    const lastComponent = components.find((c: any) => c.end == -1);
    if (!lastComponent) {
    }
    if (intUnits > lastComponent?.start) {
        return lastComponent?.cost;
    } else {
        const targetComp = components
            .filter((c: any) => c?.end != -1)
            .find((c: any) => intUnits >= c?.start && intUnits <= c?.end);
        return (targetComp?.cost ?? 0) * (row.currentUnits - row.prevUnits);
    }
};

//current month

const Reading = () => {
    const [intermediateFilter, setIntermediateFilter] = useState<any>(
        initialIntermediateFilter
    );
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 500 });
    const [filter, setFilter] = useState<any>({});
    const [billingMonth, setBillingMonth] = useState("");
    const [feedback, setFeedback] = useState<any>("");
    const [showFilterSelectionInfo, setShowFilterSelectionInfo] =
        useState(true);
    const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>(
        []
    );
    const [selectedFilters, setSelectedFilters] = useState({});
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [defaultRegions, setDefaultRegions] = useState(initialDefaultRegions);
    const { user } = useContext(AppContext);
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();

    const columnOptions = [
        { label: "Ref. No.", value: "id" },
        { label: "Customer Name", value: "name" },
        { label: "Meter No", value: "meterNo" },
        { label: "Month", value: "month" },
        { label: "None.", value: "none" },
    ];

    const orderOptions = [
        { label: "Ascending", value: "ASC" },
        { label: "Descending", value: "DESC" },
        { label: "None", value: "none" },
    ];

    const headers = useMemo(
        () => ({
            id: "Ref. No.",
            meter: "Meter",
            customer: "Customer",
            property: "Property",
            prevUnits: "Prev. Units",
            currentUnits: "Curr. Units",
            baselineUnits: "Base Lint Units",
            consumedUnits: "Cons. Units",
            issueDate: "Issue Date",
            dueDate: "Due Date",
            readingDate: "Reading Date",
            completed: "Status",
            district: "District",
            village: "Village",
            hamlet: "Hamlet",
            powerstation: "PowerStation",
            additionalCharges: "Additional Charges",
            connectionCharges: "Connection Charges",
            payedSurcharge: "Paid Surcharge",
            totalAmount: "Total Amount",
            unitsAmount: "Bill",
            lineRent: "Line Rent",
            ReadingStatus: "Reading Status",
            createdAt: "Created At",
            updatedAt: "Updated At",
            applicableSurcharge: "Surcharge",
            billMonth: "Billing Month",
            connectionType: "Connection Type",
            operator: "Operator",
            image: "Image",
            downloadImage: "Download Image",
            edit: "Edit",
            origin: "Origin",
        }),
        []
    );

    const toDateOnly = (d: Date) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const resolvePriceType = (row: any) => {
        const { priceType } = row.meter;

        if (!priceType?.replaceDate || !priceType.prevPriceType) {
            return priceType;
        }

        const readingDate = row.readingDate.split("T")[0];
        const replaceDate = priceType.replaceDate;

        return readingDate <= replaceDate ? priceType.prevPriceType : priceType;
    };

    const withEffectivePriceType = (row: any) => {
        const effectivePriceType = resolvePriceType(row);

        return {
            ...row,
            meter: {
                ...row.meter,
                priceType: effectivePriceType,
            },
        };
    };

    const calcRows = useCallback((row: any) => {
        const effectiveRow = withEffectivePriceType(row);

        const unitRule = effectiveRow.meter.priceType.unitChargeRule;

        if (unitRule.isAdaptive) {
            return effectiveRow.currentUnits - effectiveRow.prevUnits > 0
                ? getUnitPriceAdaptive(effectiveRow)[0]
                : 0;
        }

        return getUnitPrice(effectiveRow);
    }, []);

    const { data, isFetching: isLoading } = useQuery(
        ["readings", pagination.page, pagination.limit, filter],
        () => readings({ pagination, filter }),
        {
            enabled: Object.values(filter).filter((x) => Boolean(x)).length > 0,
            onSuccess(res) {
                if (res.data.rows.length === 0) {
                    return;
                }

                if (res.data) {
                    setRowsCount(res.data.count);

                    console.log(
                        "line rent: ",
                        res.data.rows.reduce((prev: any, curr: any) => {
                            return {
                                ...prev,
                                lineRent: (prev.lineRent || 0) + curr.lineRent,
                                consumedUnits:
                                    (prev.consumedUnits || 0) +
                                    (curr.currentUnits - curr.prevUnits),
                            };
                        }, {})
                    );

                    const _selectedFills = getSelectedFilters(
                        res.data.rows[0],
                        ["district", "village", "hamlet", "powerstation"],
                        filter,
                        {
                            district: { label: "District", valueKey: "name" },
                            village: { label: "Village", valueKey: "name" },
                            hamlet: { label: "Hamlet", valueKey: "name" },
                            powerstation: {
                                label: "Power Station",
                                valueKey: "name",
                            },
                        }
                    );

                    setSelectedFilters(() => _selectedFills);
                }
            },
            onError(res: any) {
                setFeedback(res.response.data.message);
            },
        }
    );

    const { data: reportTemps } = useQuery(
        ["report-templates"],
        () => getReportTemplates(user.id, "Readings Report"),
        {
            onSuccess(res) {
                setReportTemplates(() =>
                    res.data.rows.map((item: any) => ({
                        id: item.id,
                        key: item.key,
                        value: JSON.parse(item.value),
                    }))
                );
            },
        }
    );

    const handleClearAll = () => {
        setFilter({});

        // ✅ FULL RESET — no spreading old state
        setIntermediateFilter(initialIntermediateFilter);

        // ✅ reset separate states
        setBillingMonth("");

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            property: true,
            connectionType: true,
            operator: true,
        });

        // reset pagination
        setPagination({ page: 0, limit: 500 });

        // turn flags off after render
        setTimeout(() => {
            setClearAll({
                district: false,
                village: false,
                hamlet: false,
                powerstation: false,
                property: false,
                connectionType: false,
                operator: false,
            });
        }, 0);
    };

    const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        setPagination((p: any) => ({ ...p, page: 0 }));
        const [year, month] = intermediateFilter.billingMonth?.split("-") ?? [
            "",
            "",
        ];
        setFilter({
            ...intermediateFilter,
            ...(intermediateFilter.billingMonth
                ? { month, year, billingMonth: undefined }
                : {}),
        });
    };

    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];

            setIntermediateFilter({
                ...intermediateFilter,
                year,
                month: parseInt(month),
            });
        }
    }, [billingMonth]);

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];

        let consumedUnits = 0;
        let lr = 0;
        let totalAmount = 0;
        let surcharge = 0;
        let unitsAmount = 0;

        for (const row of data?.data.rows) {
            consumedUnits += row["consumedUnits"];
            lr += row["lineRent"];
            totalAmount += row["totalAmount"];
            surcharge += row["applicableSurcharge"];
            unitsAmount += calcRows(row);
        }

        for (const col of cols) {
            switch (col) {
                case "consumedUnits":
                    summaryRow.push(numberFormatter.format(consumedUnits));
                    break;

                case "lineRent":
                    summaryRow.push(currencyFormatter.format(lr));
                    break;

                case "totalAmount":
                    summaryRow.push(currencyFormatter.format(totalAmount));
                    break;

                case "applicableSurcharge":
                    summaryRow.push(currencyFormatter.format(surcharge));
                    break;

                case "unitsAmount":
                    summaryRow.push(currencyFormatter.format(unitsAmount));
                    break;

                default:
                    summaryRow.push("");
            }
        }

        if (user.ops.includes("DELETE READING")) {
            summaryRow.push("");
        }

        return summaryRow;
    };

    useEffect(() => {
        setIntermediateFilter({ ...intermediateFilter, meterStatus: "active" });
    }, []);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box>
                <Card
                    elevation={0}
                    sx={{
                        bgcolor: theme.palette.common.white,
                        p: 2,
                        mb: "1rem",
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1.5}
                        mb={1.5}
                    >
                        <Grid item xs={12} md="auto">
                            <Typography variant="h5" color="GrayText">
                                Readings
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                            container
                            spacing={1.5}
                            justifyContent="flex-end"
                        >
                            <Grid item flex={1}></Grid>

                            <Grid item xs={12} md="auto">
                                <MultiPermissionAuthorize
                                    ops={["CREATE READING"]}
                                >
                                    <Link
                                        to="/bills/readings/add"
                                        style={{ textDecoration: "none" }}
                                    >
                                        <Button
                                            sx={{ height: "100%" }}
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<Add />}
                                            fullWidth
                                        >
                                            add reading
                                        </Button>
                                    </Link>
                                </MultiPermissionAuthorize>
                            </Grid>
                        </Grid>
                    </Grid>

                    <ReadingFilterForm
                        intermediateFilter={intermediateFilter}
                        setIntermediateFilter={setIntermediateFilter}
                        billingMonth={billingMonth}
                        setBillingMonth={setBillingMonth}
                        clearAll={clearAll}
                        handleClearAll={handleClearAll}
                        handleSubmit={handleSubmit}
                        isLoading={isLoading}
                    />
                </Card>

                <Box
                    sx={{
                        mt: 1.5,
                        mb: 3,
                        p: 1.5,
                        borderLeft: `4px solid ${theme.palette.secondary.main}`,
                        bgcolor: "grey.50",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="body2">
                        This report contains meter reading information,
                        including Reference Number, Customer Name, Meter Number,
                        Billing Month, Previous Units, Current Units, Base Line
                        Units, Units Consumed, Current Month Bill Amount, Line
                        Rent, Surcharge, Total Amount (excluding arrears),
                        Reading Status, Due Date, Meter Reading Image, Operator
                        Details, and Record Status.
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.5,
                            direction: "rtl",
                            textAlign: "right",
                        }}
                    >
                        اس رپورٹ میں میٹر ریڈنگ کی معلومات شامل ہیں، جن میں
                        ریفرنس نمبر، صارف کا نام، میٹر نمبر، بلنگ ماہ، سابقہ
                        یونٹس، موجودہ یونٹس، بیس لائن یونٹس، استعمال شدہ یونٹس،
                        موجودہ ماہ کے بل کی رقم، لائن رینٹ، سرچارج، بقایا جات کے
                        بغیر کل رقم، ریڈنگ کی حیثیت، مقررہ تاریخ، میٹر ریڈنگ کی
                        تصویر، آپریٹر کی تفصیلات اور ریکارڈ کی حیثیت شامل ہیں۔
                    </Typography>
                </Box>

                {!Boolean(
                    Object.values(filter).filter((x) => Boolean(x)).length
                ) && <Alert severity="info">Please select a filter.</Alert>}

                {data?.data.rows.length == 0 && (
                    <Alert severity="info">Found 0 readings.</Alert>
                )}
            </Box>

            <Box sx={{ height: "auto", mt: 2 }}>
                <BaseTable
                    headers={headers}
                    defaultSelectedHeaders={[
                        "id",
                        "customer",
                        "meter",
                        "billMonth",
                        "prevUnits",
                        "currentUnits",
                        "baselineUnits",
                        "consumedUnits",
                        "unitsAmount",
                        "lineRent",
                        "applicableSurcharge",
                        "totalAmount",
                        "ReadingStatus",
                        "dueDate",
                        "surcharge",
                        "image",
                        "downloadImage",
                        "operator",
                        "completed",
                        "edit",
                        "createdAt",
                        "updatedAt",
                    ]}
                    load
                    data={data?.data.rows.map((row: any, serialNo: number) => ({
                        ...row,
                        billingMonth: monthFormatter.format(
                            new Date(row.year, row.month, 1)
                        ),
                        issueDate: dateFormatter.format(
                            new Date(row.issueDate)
                        ),
                        dueDate: dateFormatter.format(new Date(row.dueDate)),
                        readingDate: dateFormatter.format(
                            new Date(row.readingDate)
                        ),
                        createdAt: formatDateTime(row.createdAt),
                        updatedAt: formatDateTime(row.updatedAt),
                        applicableSurcharge: currencyFormatter.format(
                            row.applicableSurcharge
                        ),

                        meter: row.meter?.meterNo,
                        additionalCharges: row?.additionalCharges.length ? (
                            <AdditionalChargesCell
                                charges={row.additionalCharges}
                            />
                        ) : (
                            0
                        ),
                        connectionCharges: currencyFormatter.format(
                            row?.meter?.connectionCharges ?? 0
                        ),
                        property: row.property?.name,
                        completed: row.completed ? (
                            <Chip
                                sx={{ width: "10ch" }}
                                label="Done"
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        ) : (
                            <Chip
                                sx={{ width: "10ch" }}
                                label="Pending"
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        ),

                        origin:
                            row.origin == "web-app" ? (
                                <Chip
                                    sx={{ width: "10ch" }}
                                    label="Desktop"
                                    size="small"
                                    variant="outlined"
                                />
                            ) : (
                                <Chip
                                    sx={{ width: "10ch" }}
                                    label="Mobile app"
                                    size="small"
                                    variant="outlined"
                                />
                            ),

                        district: row?.district?.name ?? "",
                        village: row?.village?.name ?? "",
                        hamlet: row?.hamlet?.name ?? "",
                        powerstation: row?.meter?.powerstation?.name ?? "",
                        connectionType: row?.meter?.connectionType?.name ?? "",
                        // powerstation: row?.powerstation?.name ?? "",

                        payedSurcharge: currencyFormatter.format(
                            row.payedSurcharge
                        ),
                        consumedUnits: numberFormatter.format(
                            row.consumedUnits
                        ),
                        totalAmount: currencyFormatter.format(row.totalAmount),
                        customer: row?.property?.customer?.name,
                        unitsAmount: calcRows(row),
                        lineRent: currencyFormatter.format(row.lineRent),
                        operator: row.operator.name,
                        billMonth: monthFormatter.format(
                            new Date(row.year ?? 0, row.month - 1 || 0, 1)
                        ),
                        ReadingStatus:
                            row.wrongReadingFlag == false
                                ? "Correct"
                                : "Incorrect",
                        baselineUnits: row.baselineUnits,
                        image: <ShowReadingImage reading={row} />,
                        downloadImage: (
                            <MultiPermissionAuthorize
                                ops={["DOWNLOAD READING_IMAGE"]}
                            >
                                <DownloadReadingImage reading={row} />
                            </MultiPermissionAuthorize>
                        ),
                        edit: row.completed ? (
                            <IconButton size="small" disabled>
                                <EditOffOutlined fontSize="small" />
                            </IconButton>
                        ) : (
                            <MultiPermissionAuthorize ops={["UPDATE READING"]}>
                                <Link
                                    to={`/bills/readings/update?id=${row.id}&bulk=false&month=${row.month}&year=${row.year}&meterNo=${row.meter.meterNo}`}
                                    target="_blank"
                                    state={{
                                        ...row,
                                        bulkUpadte: false,
                                    }}
                                >
                                    <IconButton size="small">
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </Link>
                            </MultiPermissionAuthorize>
                        ),
                    }))}
                    _printables={[
                        "id",
                        "meter",
                        "customer",
                        "property",
                        "prevUnits",
                        "currentUnits",
                        "consumedUnits",
                        "issueDate",
                        "dueDate",
                        "readingDate",
                        "completed",
                        "district",
                        "village",
                        "powerstation",
                        "hamlet",
                        "additionalCharges",
                        "connectionCharges",
                        "payedSurcharge",
                        "totalAmount",
                        "lineRent",
                        "operator",
                        "applicableSurcharge",
                        "billMonth",
                        "connectionType",
                    ]}
                    delEndPoint="/reading"
                    feedback={feedback}
                    setFeedback={setFeedback}
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    permissions={{
                        edit: [""],
                        delete: ["DELETE READING"],
                    }}
                    queryKey={[
                        "readings",
                        pagination.page,
                        pagination.limit,
                        intermediateFilter,
                    ]}
                    reportTemplates={reportTemplates}
                    hasReportTemplates
                    width={1920}
                    summaryRow={createSummary}
                    reportName="Readings__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                    showSelection={["DELETE READING"]}
                />
            </Box>
        </Box>
    );
};

export default Reading;

type ACCellProps = {
    charges: {
        amount: number;
        head: { editable: boolean; amount: number; name: string };
    }[];
};

const AdditionalChargesCell = ({ charges }: ACCellProps) => {
    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            {charges.map((charge, idx) => {
                const amount = charge.head.editable
                    ? charge.amount
                    : charge.head.amount;
                return (
                    <div key={idx}>
                        <strong style={{ whiteSpace: "nowrap" }}>
                            {charge.head.name}: {amount}
                        </strong>
                    </div>
                );
            })}
        </div>
    );
};
